import React from 'react';
import litvik_logo from "./img/litvik_logo.jpg";
import { useState, useEffect } from "react";
import './Login.css';
import { useCookies } from 'react-cookie';
import { AiOutlineMail, AiOutlinePhone } from "react-icons/ai";
import { FaWhatsapp } from "react-icons/fa";
import LoginPlantMap from './components/LoginPlantMap';
import { getBgImageLogo, submitLogin } from './services/LoginServices';
import { clearAuthSession, getApiBaseUrl } from "./services/serviceconfig";
import { getUserAllowedPlants } from './services/PlantServices';
import { displayError, getLoginErrorMessage } from './helpers';
import { resolveLoginCompany } from './utils/loginCompany';
import Swal from "sweetalert2";
import {
	AUTH_COOKIE_NAMES,
	consumeSessionExpiredNotice,
	hasBrowserSession,
	isAuthenticated,
	isIdleExpired,
	markSessionActive,
	markSessionExpiredNotice,
	SESSION_EXPIRED_MESSAGE,
} from './authSession';

const REGISTERED_OFFICE_ID = 'registered-office';

function defaultLocationId(locations) {
	const plant = (locations || []).find((location) => String(location.id || '').startsWith('plant-'));
	return plant?.id || locations?.[0]?.id || '';
}

function buildGeocodeQueries(address) {
	const cleaned = (address || '')
		.replace(/\(\d+\)/g, '')
		.replace(/,\s*,/g, ',')
		.replace(/\s+/g, ' ')
		.trim();
	if (!cleaned || cleaned === 'Address not available') {
		return [];
	}
	const queries = [cleaned];
	const pin = cleaned.match(/\b\d{6}\b/);
	if (pin) {
		queries.push(`${pin[0]}, Tamil Nadu, India`);
	}
	const parts = cleaned.split(',').map((part) => part.trim()).filter(Boolean);
	if (parts.length >= 2) {
		queries.push(`${parts.slice(-3).join(', ')}, India`);
	}
	return [...new Set(queries)];
}

async function geocodePlantAddress(address) {
	try {
		const queries = buildGeocodeQueries(address);
		for (const query of queries) {
			const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&q=${encodeURIComponent(query)}`;
			const response = await fetch(url, { headers: { Accept: 'application/json' } });
			if (!response.ok) {
				continue;
			}
			const data = await response.json();
			const hit = data?.[0];
			if (!hit) {
				continue;
			}
			const lat = Number(hit.lat);
			const lng = Number(hit.lon);
			if (Number.isFinite(lat) && Number.isFinite(lng)) {
				return { lat, lng };
			}
		}
		return null;
	} catch {
		return null;
	}
}

function buildLoginLocations(company, plants) {
	const locations = [];
	const officeAddress = (company?.address || '').trim();
	if (officeAddress || company?.name) {
		locations.push({
			id: REGISTERED_OFFICE_ID,
			name: 'Head Office',
			area: officeAddress || 'Address not available',
		});
	}
	(plants || []).forEach((plant, index) => {
		const address = (plant.address || '').trim();
		const plantName = (plants.length === 1)
			? 'Plant Location'
			: (plant.alias || plant.name || `Plant ${index + 1}`);
		locations.push({
			id: `plant-${plant.id}`,
			name: plantName,
			area: address || 'Address not available',
		});
	});
	return locations;
}

const FEATURES = [
	{ title: 'Marketing Management', desc: 'Customer, project, and sales order workflows' },
	{ title: 'Planning Management', desc: 'Schedule, production, and dispatch coordination' },
	{ title: 'Dispatch Management', desc: 'Delivery challan, vehicle allocation, and site delivery tracking' },
	{ title: 'Accounting Management', desc: 'Invoice, receipts, payments, banking, adjustments' },
	{ title: 'Fleet Management', desc: 'Trucks, pumps, drivers, diesel, and fleet reports' },
	{ title: 'Quality Control', desc: 'Material analysis, design mix, and QC reports' },
	{ title: 'Inventory Management', desc: 'Vendor, purchase order, GRN, delivery, and stock' },
	{ title: 'Maintenance Management', desc: 'Equipment service, breakdowns, and preventive maintenance' },
];

function Login() {
	const imageUrl = getApiBaseUrl().slice(0, -4);
	const [inputs, setInputs] = useState({});
	const [tfa, setTfa] = useState({
		secret_code: '',
		qrcode: '',
		otp: '',
	});
	const [error, setError] = useState({});
	const [isLoggingIn, setIsLoggingIn] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [loginMessage, setLoginMessage] = useState('');
	const [showTfa, setShowTfa] = useState(false);
	const [showQrCode, setShowQrCode] = useState(true);
	const [cookies, setCookie, removeCookie] = useCookies(AUTH_COOKIE_NAMES);
	const [position, setPosition] = useState({ latitude: 0, longitude: 0 });
	const [companyLogo, setCompanyLogo] = useState("");
	const [companyName, setCompanyName] = useState("");
	const [activePlant, setActivePlant] = useState('');
	const [plantLocations, setPlantLocations] = useState([]);

	const getCompanyLogoSrc = () => {
		if (!companyLogo) {
			return litvik_logo;
		}
		return companyLogo.startsWith('http') ? companyLogo : `${imageUrl}${companyLogo}`;
	};

	useEffect(() => {
		let cancelled = false;
		getBgImageLogo()
			.then(async (response) => {
				if (response?.apiOffline) {
					return;
				}
				const companies = (response?.company_list || []).filter((obj) => obj.status);
				const company = resolveLoginCompany(companies)
					|| companies.find((item) => item.logo)
					|| companies[0];
				if (cancelled) {
					return;
				}
				if (company?.logo) {
					setCompanyLogo(company.logo);
				}
				if (company?.name) {
					setCompanyName(company.name);
				}
				const locations = buildLoginLocations(company, response?.plant_list || []);
				const plantId = defaultLocationId(locations);
				setPlantLocations(locations);
				if (plantId) {
					setActivePlant(plantId);
				}
				if (!locations.length) {
					return;
				}
				const plantsFirst = [
					...locations.filter((location) => String(location.id || '').startsWith('plant-')),
					...locations.filter((location) => !String(location.id || '').startsWith('plant-')),
				];
				let current = locations;
				for (let index = 0; index < plantsFirst.length; index += 1) {
					const location = plantsFirst[index];
					const coords = await geocodePlantAddress(location.area);
					if (cancelled) {
						return;
					}
					if (coords) {
						current = current.map((item) => (
							item.id === location.id ? { ...item, ...coords } : item
						));
						setPlantLocations(current);
					}
					if (index < plantsFirst.length - 1) {
						await new Promise((resolve) => window.setTimeout(resolve, 1100));
					}
				}
			})
			.catch((loadError) => {
				console.warn('Login page logo load failed:', loadError?.message || loadError);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const options = {
		enableHighAccuracy: true,
		timeout: 5000,
		maximumAge: 0,
	};

	function success(pos) {
		const crd = pos.coords;
		setPosition({
			latitude: crd.latitude,
			longitude: crd.longitude,
		});
	}

	function errors(err) {
		console.warn(`ERROR(${err.code}): ${err.message}`);
	}

	const handleChange = (event) => {
		const name = event.target.name;
		const value = event.target.value;

		if (name === 'username' || name === 'password') {
			setInputs((values) => ({ ...values, [name]: value }));
			if (error[name]) {
				setError((values) => ({ ...values, [name]: '' }));
			}
			if (loginMessage) {
				setLoginMessage('');
			}
			return;
		}

		if (name === 'otp') {
			setTfa((values) => ({ ...values, [name]: value }));
			if (error.otp) {
				setError((values) => ({ ...values, otp: '' }));
			}
			if (loginMessage) {
				setLoginMessage('');
			}
		}
	};

	const validateLoginForm = (username, password, otpValue) => {
		const nextErrors = {};

		if (showTfa) {
			if (!(otpValue || '').trim()) {
				nextErrors.otp = 'OTP must not be empty';
			}
			return nextErrors;
		}

		if (!(username || '').trim()) {
			nextErrors.username = 'User Name must not be empty';
		}
		if (!(password || '').trim()) {
			nextErrors.password = 'Password must not be empty';
		}

		return nextErrors;
	};

	useEffect(() => {
		if (navigator.geolocation) {
			navigator.permissions
				.query({ name: "geolocation" })
				.then((result) => {
					if (result.state === "granted") {
						navigator.geolocation.getCurrentPosition(success);
					} else if (result.state === "prompt") {
						navigator.geolocation.getCurrentPosition(success, errors, options);
					} else if (result.state === "denied") {
						console.log("If denied then you have to show instructions to enable location");
					}
				})
				.catch((geoError) => {
					console.warn('Geolocation permission check failed:', geoError?.message || geoError);
				});
		} else {
			Swal.fire("Sorry Not available!", "", "info");
		}
	}, []);

	useEffect(() => {
		if (consumeSessionExpiredNotice()) {
			Swal.fire('Session Expired', SESSION_EXPIRED_MESSAGE, 'info');
		}

		if (!cookies?.myToken) {
			return;
		}

		// Tab/browser was closed — sessionStorage cleared; require fresh login.
		if (!hasBrowserSession()) {
			clearAuthSession();
			AUTH_COOKIE_NAMES.forEach((cookieName) => {
				removeCookie(cookieName, { path: '/' });
			});
			return;
		}

		if (isIdleExpired()) {
			markSessionExpiredNotice();
			clearAuthSession();
			AUTH_COOKIE_NAMES.forEach((cookieName) => {
				removeCookie(cookieName, { path: '/' });
			});
			Swal.fire('Session Expired', SESSION_EXPIRED_MESSAGE, 'info');
			return;
		}

		if (isAuthenticated(cookies)) {
			getUserAllowedPlants(cookies)
				.then(() => {
					window.location.href = '/Home';
				})
				.catch(() => {
					clearAuthSession();
					AUTH_COOKIE_NAMES.forEach((cookieName) => {
						removeCookie(cookieName, { path: '/' });
					});
				});
		}
	}, []);

	const handleSubmit = (event) => {
		event.preventDefault();
		// Read from the DOM so browser autofill is included (controlled React state often misses it).
		const form = event.currentTarget;
		const username = String(form.username?.value ?? inputs.username ?? '').trim();
		const password = String(form.password?.value ?? inputs.password ?? '');
		const otpValue = String(form.otp?.value ?? tfa.otp ?? '').trim();

		const validationErrors = validateLoginForm(username, password, otpValue);
		setError(validationErrors);
		if (Object.keys(validationErrors).length > 0) {
			setLoginMessage(Object.values(validationErrors).join(' '));
			return;
		}

		if (!showTfa) {
			setInputs((values) => ({ ...values, username, password }));
		}

		setLoginMessage('');
		setIsLoggingIn(true);
		submitLogin(
			username || String(inputs.username || '').trim(),
			password || inputs.password || '',
			otpValue,
			position.latitude,
			position.longitude,
		)
			.then((login_response) => {
				if (login_response.tfa_enabled === false) {
					setLoginMessage('');
					setTfa((values) => ({
						...values,
						secret_code: login_response.otp_base32,
						qrcode: login_response.img_str,
					}));
					setInputs((values) => ({ ...values, username: login_response.username }));
					setShowQrCode(true);
					setShowTfa(true);
				} else if (login_response.tfa_enabled === true && login_response.state === 'have to verify otp') {
					setLoginMessage('');
					setShowQrCode(false);
					setShowTfa(true);
				} else if (login_response.token) {
					setLoginMessage('');
					const cookieOptions = { path: '/' };
					setCookie('myToken', login_response.token, cookieOptions);
					setCookie('myName', login_response.name, cookieOptions);
					if (login_response.user_id) {
						setCookie('myUserId', login_response.user_id, cookieOptions);
					}

					if (!login_response.is_superuser) {
						setCookie('myCategory', login_response.category, cookieOptions);
						removeCookie('mySuperuser', { path: '/' });
					} else {
						setCookie('mySuperuser', 'true', cookieOptions);
						removeCookie('myCategory', { path: '/' });
					}

					markSessionActive();
					window.location.href = '/Home';
				} else {
					const failureMessage = typeof login_response?.detail === 'string'
						? login_response.detail
						: 'Login could not be completed. Contact your administrator.';
					setLoginMessage(failureMessage);
					displayError(login_response, 'Login Failed');
				}
			})
			.catch((loginError) => {
				console.log(loginError?.response?.data || loginError);
				const failureMessage = getLoginErrorMessage(loginError, getApiBaseUrl());
				setLoginMessage(failureMessage);
				displayError(
					loginError?.response?.data || failureMessage,
					"Login Failed",
				);
			})
			.finally(() => {
				setIsLoggingIn(false);
			});
	};

	return (
		<div className="login-page">
			{!showTfa ? (
				<div className="login-page__grid">
					<aside className="login-card login-card--features">
						<span className="login-card__eyebrow">Modules</span>
						<h2 className="login-card__title">ERP Features</h2>
						<p className="login-card__lead">
							Litvik ERP simplifies ready-mix concrete operations with integrated daily workflows.
						</p>
						<ul className="login-feature-list">
							{FEATURES.map((feature) => (
								<li key={feature.title} className="login-feature-list__item">
									<span className="login-feature-list__bullet" />
									<div>
										<span className="login-feature-list__title">{feature.title}</span>
										<span className="login-feature-list__desc">{feature.desc}</span>
									</div>
								</li>
							))}
						</ul>
					</aside>

					<main className="login-card login-card--form">
						<div className="login-card__avatar-wrap">
							<img
								src={getCompanyLogoSrc()}
								alt={companyName || 'Company logo'}
								className="login-card__avatar"
								onError={(event) => {
									event.currentTarget.onerror = null;
									event.currentTarget.src = litvik_logo;
								}}
							/>
						</div>
						{companyName ? (
							<h2 className="login-card__company-name">{companyName}</h2>
						) : null}
						<form className="login-form" onSubmit={handleSubmit}>
							{loginMessage ? (
								<div className="login-alert login-alert--danger" role="alert">
									<strong>Login failed:</strong> {loginMessage}
								</div>
							) : null}
							<div className="login-form__group">
								<label className="login-form__label" htmlFor="username">User Name</label>
								<div className="login-form__field">
									<span className="login-form__field-icon"><i className="fa fa-user" /></span>
									<input
										id="username"
										className="login-form__input"
										type="text"
										name="username"
										required
										autoComplete="username"
										onChange={handleChange}
										onInput={handleChange}
										value={inputs.username || ""}
										placeholder="Enter username"
									/>
								</div>
								{error.username ? <p className="login-form__error">{error.username}</p> : null}
							</div>

							<div className="login-form__group">
								<label className="login-form__label" htmlFor="password">Password</label>
								<div className="login-form__field">
									<span className="login-form__field-icon"><i className="fa fa-lock" /></span>
									<input
										id="password"
										className="login-form__input login-form__input--password"
										type={showPassword ? 'text' : 'password'}
										name="password"
										required
										autoComplete="current-password"
										onChange={handleChange}
										onInput={handleChange}
										value={inputs.password || ""}
										placeholder="Enter password"
									/>
									<button
										type="button"
										className="login-form__password-toggle"
										onClick={() => setShowPassword((prev) => !prev)}
										aria-label={showPassword ? 'Hide password' : 'Show password'}
										title={showPassword ? 'Hide password' : 'Show password'}
									>
										<i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true" />
									</button>
								</div>
								{error.password ? <p className="login-form__error">{error.password}</p> : null}
							</div>

							<input
								className="login-form__submit"
								type="submit"
								value={isLoggingIn ? 'Logging in...' : 'Log In'}
								disabled={isLoggingIn}
							/>
						</form>
					</main>

					<aside className="login-card login-card--locations">
						<span className="login-card__eyebrow">Locations</span>
						<h2 className="login-card__title">Head Office and Plant Location</h2>
						<p className="login-card__lead">
							Head Office and plant addresses, shown on the map.
						</p>
						<div className="login-locations-layout">
							{plantLocations.length ? (
								<ul className="login-location-list">
									{plantLocations.map((location) => (
										<li key={location.id}>
											<button
												type="button"
												className={`login-location-list__item${activePlant === location.id ? ' is-active' : ''}`}
												onClick={() => setActivePlant(location.id)}
											>
												<span className="login-location-list__bullet" />
												<span className="login-location-list__copy">
													<span className="login-location-list__title">{location.name}</span>
													<span className="login-location-list__area">{location.area}</span>
												</span>
											</button>
										</li>
									))}
								</ul>
							) : (
								<p className="login-card__lead">Loading locations from company records…</p>
							)}
							<LoginPlantMap
								locations={plantLocations}
								activeName={activePlant}
								onSelect={setActivePlant}
							/>
						</div>
					</aside>
				</div>
			) : (
				<div className="login-page__tfa">
					<div className="login-card login-tfa-card">
						<h2 className="login-tfa-card__title">Two-Factor Authentication</h2>
						{showQrCode ? (
							<>
								<p className="login-tfa-card__hint">Scan the QR code with your authenticator app</p>
								<img
									src={`data:image/jpeg;base64,${tfa.qrcode}`}
									alt="2FA QR code"
									className="login-tfa-card__qr"
								/>
							</>
						) : (
							<p className="login-tfa-card__hint">Enter the one-time password from your authenticator app</p>
						)}
						<form className="login-form" onSubmit={handleSubmit}>
							{loginMessage ? (
								<div className="login-alert login-alert--danger" role="alert">
									<strong>Verification failed:</strong> {loginMessage}
								</div>
							) : null}
							<div className="login-form__group">
								<label className="login-form__label" htmlFor="otp">One-Time Password</label>
								<input
									id="otp"
									className="login-form__input"
									style={{ paddingLeft: '14px' }}
									type="text"
									name="otp"
									placeholder="Enter OTP"
									onChange={handleChange}
									value={tfa.otp || ""}
									required
								/>
								{error.otp ? <p className="login-form__error">{error.otp}</p> : null}
							</div>
							<button type="submit" className="login-tfa-card__submit" disabled={isLoggingIn}>
								{isLoggingIn ? 'Verifying...' : 'Verify & Activate'}
							</button>
						</form>
					</div>
				</div>
			)}

			<footer className="login-page__footer">
				<div className="login-page__footer-brand">
					<img src={litvik_logo} alt="Litvik" className="login-page__brand-logo" />
					<div className="login-page__brand-text">
						<h1 className="login-page__brand-title">Litvik Software Labs Pvt. Ltd.</h1>
					</div>
				</div>

				<div className="login-page__footer-contacts">
					<a
						className="login-page__footer-link"
						href="https://wa.me/919551614121"
						target="_blank"
						rel="noreferrer"
					>
						<span className="login-page__footer-icon login-page__footer-icon--whatsapp" aria-hidden="true">
							<FaWhatsapp size={15} />
						</span>
						<span className="login-page__footer-icon" aria-hidden="true">
							<AiOutlinePhone size={15} />
						</span>
						+91 9551614121
					</a>
					<a className="login-page__footer-link" href="mailto:support@litvik.in">
						<span className="login-page__footer-icon" aria-hidden="true">
							<AiOutlineMail size={15} />
						</span>
						support@litvik.in
					</a>
				</div>

				<div className="login-page__footer-meta">
					<span>litvik@2026-27</span>
					<span>developed &amp; maintenance by litvik</span>
				</div>
			</footer>
		</div>
	);
}

export default Login;
